import { Box, Chip, Typography } from '@mui/material'
import { FC, useEffect, useState } from 'react'
import { useGetOne, useTranslate } from 'react-admin'
import { useNavigate } from 'react-router-dom'
import { DashboardStats, LiveOpsBlock, TopErrorLink, TopMcpServer } from '../types/apiRequests'
import { border } from '../layout/theme/border'
import { xxllncColor } from '../layout/theme/color'
import { useCountUp } from '../utils/useCountUp'
import { formatCompact } from '../utils/formatNumber'
import { getJson, getUrl } from '../providers/data'
import {
  CapacityCard,
  CompactGauge,
  LiveOpsSection,
  PendingBanner,
  PlatformStat,
  SkeletonCard,
  SmallCard,
  TopErrorsList,
  TopOrganizationsList,
  WeeklyChart,
  fadeSlideIn,
} from '../components/dashboard'

const LIVE_REFRESH_MS = 30000

/* eslint-disable camelcase -- mapping to the snake_case API shape (TopErrorLink). */
const toErrorLinks = (items: LiveOpsBlock['top_error_workflows'] = []): TopErrorLink[] =>
  items.map((i) => ({
    link_id: i.link_id,
    name: i.name,
    organization_id: i.organization_id,
    organization_name: i.organization_name ?? '',
    error_count: i.error_count ?? 0,
    total_count: i.total_count ?? 0,
  }))
/* eslint-enable camelcase */

const PopularMcpsList: FC<{
  title: string
  items: TopMcpServer[]
  delay: number
  translate: (key: string) => string
}> = ({ title, items, delay, translate }) => {
  const navigate = useNavigate()

  return (
    <Box
      sx={{
        background: '#fff',
        borderRadius: border.card.radius,
        border: '1px solid rgba(0,0,0,0.06)',
        animation: `${fadeSlideIn} 0.5s ease-out ${delay}ms both`,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            color: 'rgba(0,0,0,0.3)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          {title}
        </Typography>
      </Box>
      {items.length === 0 ? (
        <Box sx={{ px: 2.5, py: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 13, color: 'rgba(0,0,0,0.25)' }}>
            {translate('dashboard.noData')}
          </Typography>
        </Box>
      ) : (
        items.map((item, i) => (
          <Box
            key={item.name}
            onClick={() => navigate(`/mcp_server/${item.id}/details`)}
            sx={{
              'px': 2.5,
              'py': 1.5,
              'display': 'flex',
              'alignItems': 'center',
              'justifyContent': 'space-between',
              'gap': 2,
              'cursor': 'pointer',
              'borderBottom':
                i < items.length - 1 ? '1px solid rgba(0,0,0,0.03)' : 'none',
              'transition': 'background 0.15s',
              '&:hover': { background: 'rgba(0,0,0,0.015)' },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#1a1a1a',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.display_name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <Chip
                label={`${item.organization_count ?? 0} ${translate('dashboard.organizations')}`}
                size="small"
                sx={{
                  fontWeight: 600, fontSize: 11, height: 22, border: 'none',
                  bgcolor: `${xxllncColor.dark100}12`, color: xxllncColor.dark100,
                }}
              />
              {(item.total_calls ?? 0) > 0 && (
                <Chip
                  label={`${formatCompact(item.total_calls ?? 0)} ${translate('dashboard.calls')}`}
                  size="small"
                  sx={{
                    fontWeight: 600, fontSize: 11, height: 22, border: 'none',
                    bgcolor: xxllncColor.opGreen10, color: xxllncColor.green100,
                  }}
                />
              )}
            </Box>
          </Box>
        ))
      )}
    </Box>
  )
}

const Dashboard: FC = () => {
  const translate = useTranslate()
  const navigate = useNavigate()
  const { data: stats, isLoading } = useGetOne<DashboardStats & { id: string }>(
    'dashboard_stats',
    { id: '1' },
  )

  const mcpLinks = useCountUp(stats?.total_mcp_links ?? 0)
  const workflowExecutions = useCountUp(stats?.recent_workflow_executions ?? 0)
  const activeSeats = useCountUp(stats?.total_active_seats ?? 0)
  const alertCount = useCountUp(stats?.active_alerts_count ?? 0)

  // Live operational block (now / last hour / last 24h) — custom non-CRUD
  // endpoint, polled so the dashboard stays actueel.
  const [live, setLive] = useState<LiveOpsBlock | null>(null)
  useEffect(() => {
    let active = true
    const load = () => {
      getJson<LiveOpsBlock>(`${getUrl()}/dashboard_stats/live`)
        .then((data) => {
          if (active) setLive(data)
        })
        .catch(() => undefined)
    }
    load()
    const timer = setInterval(load, LIVE_REFRESH_MS)
    return () => {
      active = false
      clearInterval(timer)
    }
  }, [])

  return (
    <Box sx={{ p: { xs: 3, md: 4 }, maxWidth: 1200 }}>

      {isLoading ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: 'repeat(3, 1fr)',
            },
            gap: 2,
          }}
        >
          {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
        </Box>
      ) : stats ? (
        <>
          {(stats.pending_access_requests || stats.pending_platform_requests) ? (
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5, mb: 3 }}>
              <PendingBanner
                count={stats.pending_access_requests ?? 0}
                messageKey="dashboard.pendingAccessRequests"
                href="/requests"
                accent={xxllncColor.orange100}
                accentBg={xxllncColor.opOrange20}
                delay={0}
              />
              <PendingBanner
                count={stats.pending_platform_requests ?? 0}
                messageKey="dashboard.pendingPlatformRequests"
                href="/requests"
                accent={xxllncColor.dark100}
                accentBg={xxllncColor.opDark20}
                delay={40}
              />
            </Box>
          ) : null}

          <LiveOpsSection live={live} translate={translate} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '7fr 3fr' },
              gap: 2,
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
                background: '#fff',
                borderRadius: border.card.radius,
                border: '1px solid rgba(0,0,0,0.06)',
                animation: `${fadeSlideIn} 0.4s ease-out 50ms both`,
              }}
            >
              <Box
                sx={{
                  animation: `${fadeSlideIn} 0.4s ease-out 0ms both`,
                  textAlign: 'center',
                  py: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: 36,
                    fontWeight: 700,
                    lineHeight: 1,
                    letterSpacing: '-0.03em',
                    color: xxllncColor.dark100,
                    fontVariantNumeric: 'tabular-nums',
                    mb: 0.5,
                  }}
                >
                  {stats.active_workflows ?? 0}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'rgba(0,0,0,0.35)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                  }}
                >
                  {translate('dashboard.activeWorkflows')}
                </Typography>
              </Box>
              <PlatformStat
                value={activeSeats}
                label={translate('dashboard.activeSeats')}
                accent={xxllncColor.dark100}
                delay={40}
              />
              <PlatformStat
                value={mcpLinks}
                label={translate('dashboard.mcpLinks')}
                accent={xxllncColor.dark100}
                delay={80}
              />
              <PlatformStat
                value={workflowExecutions}
                label={translate('dashboard.recentExecutions')}
                accent={xxllncColor.orange100}
                delay={120}
              />
            </Box>
            <CapacityCard capacity={stats.capacity ?? null} delay={160} />
          </Box>

          {stats?.active_alerts_count ? (
            <Box
              onClick={() => navigate('/monitoring_alert')}
              sx={{
                'display': 'inline-flex',
                'alignItems': 'center',
                'gap': 1,
                'cursor': 'pointer',
                'background': xxllncColor.opRed10,
                'borderRadius': border.card.radius,
                'border': '1px solid rgba(0,0,0,0.06)',
                'px': 2.5,
                'py': 1.5,
                'mb': 3,
                'animation': `${fadeSlideIn} 0.4s ease-out 160ms both`,
                'transition': 'background 0.15s',
                '&:hover': { bgcolor: xxllncColor.opRed20 },
              }}
            >
              <Typography
                sx={{
                  fontSize: 24,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: xxllncColor.red100,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {alertCount}
              </Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: xxllncColor.opRed60,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                {translate('dashboard.activeAlerts')}
              </Typography>
            </Box>
          ) : null}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              mt: 3,
            }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <SmallCard delay={400}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: xxllncColor.opDark60,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    mb: 1,
                  }}
                >
                  MCP
                </Typography>
                <Typography
                  sx={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: '#1a1a1a',
                    fontVariantNumeric: 'tabular-nums',
                    mb: 0.5,
                  }}
                >
                  {(stats.mcp_total_calls ?? 0).toLocaleString('nl-NL')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'rgba(0,0,0,0.35)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}
                >
                  {translate('dashboard.totalCalls')}
                </Typography>
              </SmallCard>
              <CompactGauge rate={stats.mcp_error_rate} label="MCP fouten" delay={440} />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <SmallCard delay={480}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: xxllncColor.opOrange60,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    mb: 1,
                  }}
                >
                  Workflow
                </Typography>
                <Typography
                  sx={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: '#1a1a1a',
                    fontVariantNumeric: 'tabular-nums',
                    mb: 0.5,
                  }}
                >
                  {(stats.workflow_total_executions ?? 0).toLocaleString('nl-NL')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'rgba(0,0,0,0.35)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}
                >
                  {translate('dashboard.totalExecutions')}
                </Typography>
              </SmallCard>
              <CompactGauge
                rate={stats.workflow_error_rate ?? 0}
                label="Workflow fouten"
                delay={520}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              mt: 3,
            }}
          >
            <TopOrganizationsList
              title={translate('dashboard.topUsageOrgs')}
              items={stats.top_usage_organizations ?? []}
              accent={xxllncColor.dark100}
              delay={560}
            />
            <PopularMcpsList
              title={translate('dashboard.popularMcps')}
              items={stats.top_popular_mcps ?? []}
              delay={600}
              translate={translate}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              mt: 3,
            }}
          >
            <TopErrorsList
              title={translate('dashboard.live.mcpErrorsTitle')}
              items={toErrorLinks(live?.top_error_mcp_servers ?? [])}
              linkPrefix="mcp_server"
              hrefBuilder={(item) => `/mcp_server/${item.link_id}/details`}
              accent={xxllncColor.red100}
              delay={640}
            />
            <TopErrorsList
              title={translate('dashboard.live.workflowErrorsTitle')}
              items={toErrorLinks(live?.top_error_workflows ?? [])}
              linkPrefix="organization_workflow_link"
              hrefBuilder={(item) =>
                item.organization_id
                  ? `/organization/${item.organization_id}/show/logs`
                  : null
              }
              accent={xxllncColor.red100}
              delay={680}
            />
          </Box>

          {/* Weekly traffic — demoted to the bottom (the dashboard is now live-first). */}
          <Box sx={{ mt: 3, opacity: 0.85 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: 'rgba(0,0,0,0.3)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                mb: 1,
              }}
            >
              {translate('dashboard.live.weeklyArchive')}
            </Typography>
            <WeeklyChart
              daily={stats.daily ?? []}
              mcpWeek={stats.mcp_requests_this_week}
              workflowWeek={stats.workflow_requests_this_week ?? 0}
              delay={720}
              translate={translate}
            />
          </Box>
        </>
      ) : null}
    </Box>
  )
}

export default Dashboard
